import {halClient} from '../../index';
import {BBModel} from "../../bb_model";
import {BaseModel} from "../base";
import {company} from "../../services/company";
import {context} from "../../constants/context";
import sessionStorage from '../../utils/storage/session_storage';
import localStorage from '../../utils/storage/local_storage';
import {PusherFactory} from "../../index";

class CompanyModel extends BaseModel {


    constructor(data) {
        super(data);
        if (this.address) {
            this.address = new BBModel.Address(this.address);
        }
        this._setChildCompanies();
    }

    ignoreKeys() {
        return super.ignoreKeys().concat(['companies', 'pusher', 'pusherChannel', 'settings']);
    }

    _setChildCompanies() {

        if (!this.companies) return;

        /**
         * @type {Array<BBModel.Company>} Direct ancestors
         */
        this.child_companies = [];

        for (const childCompanyRaw of Array.from(this.companies)) {
            const childCompany = new BBModel.Company(halClient.$parse(childCompanyRaw));
            this.child_companies.push(childCompany);
        }

        this._setAllCompanies();
    }

    _setAllCompanies(){
        const allCompanies = [];

        for (const childCompany of this.child_companies) {
            if (childCompany.companies) {
                for (const grandChildCompany of Array.from(childCompany.companies)) {
                    allCompanies.push(grandChildCompany);
                }
            } else {
                allCompanies.push(childCompany);
            }
        }

        /**
         * @type {Array<BBModel.Company|BBModel.Admin.Company>} All ancestors
         */
        this.companies = allCompanies;
    }

    /**
     * @description
     * Find a child company by reference
     *
     * @returns {promise} A promise for the child company
     */
    getCompanyByRef(ref) {

        return new Promise((resolve, reject) => {
            this.$get('companies').then((companies) => {
                    const company = _.find(companies, c => c.reference === ref);
                    if (company) {
                        resolve(company);
                    } else {
                        reject(`No company for ref ${ref}`);
                    }
                }
                , (err) => {
                    console.log('err ', err);
                    return reject(err);
                });
        });
    }

    /**
     * @description
     * Find a child company by id
     *
     * @returns {object} The child company
     */
    findChildCompany(id) {

        if (!this.hasChildren()) {
            return null;
        }

        if (this.companies) {
            for (const c of Array.from(this.companies)) {
                if (c.id === parseInt(id)) {
                    return c;
                }
                if (c.ref && (c.ref === String(id))) {
                    return c;
                }
            }
        }

        // failed to find by id - maybe by name ?
        if (typeof id === 'string') {
            const name = id.replace(/[\s\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|'’!<>;:,.~`=+-@£&%"]/g, '').toLowerCase();
            for (const c of Array.from(this.companies)) {
                const cname = c.name.replace(/[\s\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|'’!<>;:,.~`=+-@£&%"]/g, '').toLowerCase();
                if (name === cname) {
                    return c;
                }
            }
        }

        return null;
    }

    /**
     * @description
     * Get settings company
     *
     * @returns {promise} A promise for settings company
     */
    getSettings() {
        return new Promise((resolve, reject) => {
            if (this.settings) {
                resolve(this.settings);
            } else {
                if (this.$has('settings')) {
                    this.$get('settings').then((set) => {
                            this.settings = new BBModel.CompanySettings(set);
                            resolve(this.settings);
                        }
                    );
                } else {
                    reject('Company has no settings');
                }
            }
        });
    }

    /**
     * @description
     * Push subscribe for company
     *
     * @returns {object|undefined} Subscriber company
     */
    pusherSubscribe(callback, options = {}) {

        if (!this.pusher && this.$has('pusher')) {

            this.pusher = PusherFactory.create({
                encrypted: options.hasOwnProperty('encrypted') ? options.encrypted : true,
                authEndpoint: this.$link('pusher').href,
                auth: {
                    headers: {
                        'App-Id': context.appId,
                        'App-Key': context.appKey,
                        'Auth-Token': localStorage.getItem('auth_token')
                    }
                }
            });
        }

        if (!this.pusher) {
            return;
        }

        const channelName = `private-c${this.id}-w${this.numeric_widget_id}`;

        // Nuke the channel if it exists, must be done if this is to be used in multiple pages
        // this is being delt differently in the newer implementation
        if (this.pusher.channel(channelName) != null) {
            this.pusher.unsubscribe(channelName);
        }

        this.pusher_channel = this.pusher.subscribe(channelName);
        this.pusher_channel.bind('booking', callback);
        this.pusher_channel.bind('cancellation', callback);
        return this.pusher_channel.bind('updating', callback);
    }

    /**
     * @returns {object|undefined} Pusher channel
     */
    getPusherChannel(model, options = {}) {

        if (!this.pusher && this.$has('pusher')) {

            this.pusher = PusherFactory.create({
                encrypted: options.hasOwnProperty('encrypted') ? options.encrypted : true,
                authEndpoint: this.$link('pusher').href,
                auth: {
                    headers: {
                        'App-Id': context.appId,
                        'App-Key': context.appKey,
                        'Auth-Token': localStorage.getItem('auth_token')
                    }
                }
            });
        }

        if (!this.pusher) {
            return;
        }

        if (this.$has(model)) {
            let channelName = this.$href(model);
            channelName = channelName.replace(/https?:\/\//, '').replace(/\//g, '-').replace(/:/g, '_');
            if (this.pusher.channel(channelName)) {
                return this.pusher.channel(channelName);
            } else {
                this.pusher.subscribe(channelName);
                return this.pusher.channel(channelName);
            }
        }
    }

    getOpeningHours() {
        return new Promise((resolve, reject) => {
            if (this.$has('opening_hours')) {
                this.$get('opening_hours').then((openingHours) => {
                    this.openingHours = openingHours.openingHoursSpecification;
                    this.formatOpeningHoursSchema();
                    resolve(this.openingHours);
                }, (err) => {
                    console.info('error', err);
                    reject(err);
                });
            }

        });
    }

    formatOpeningHoursSchema() {
        if (!this.openingHours) return;
        this.openingHours.forEach((day) => {
            // http://schema.org/Monday => monday
            day.dayKey = day.dayOfWeek.replace('http://schema.org/', '').toUpperCase();
            // create moment objects from time strings passed in format 'HH:mm:ss'
            if (day.opens) {
                day.opens = moment(day.opens, 'HH:mm:ss');
            }

            if (day.closes) {
                day.closes = moment(day.closes, 'HH:mm:ss');
            }
        });
    }

    hasChildren() {
        return this.$has('children') || !!(this.companies && this.companies.length);
    }

    static $query(company_id, options) {
        return company.query(company_id, options);
    }


    static $queryByRef(reference, options) {
        return company.queryByRef(reference, options);

    }


    getApp(name){
        return new Promise((resolve, reject) => {
            this.$get('apps', {app_name: name}).then( (app) => {
                return resolve(new BBModel.JRNIApp(app));
            }, (err) => {
                return reject(err);
            });
        });
    }

}

export {
    CompanyModel
}
